import React, { useState } from 'react';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Chip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Cancel, MoreVert } from '@material-ui/icons';
import { formatDate } from '@roc/feature-utils';
import { useDrawsStore } from '@roc/feature-draws';
import { useUserStore } from '@roc/feature-app-core';
import {
  DataGrid,
  ConfirmDialog,
  StandardDialog,
  AgGridColumnProps,
  MenuItem,
  Button
} from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { DRAW_INIT_STAGE, DRAW_REQUEST_STAGE } from '../../constants/draws';
import { Draw } from '../../types/draws';
import { DrawsAccordion } from './drawsAccordion';
import ViewPropertyDetails from './viewPropertyDetails';
import { getDrawNumber } from '../../utils/utils';

const useStyles = makeStyles(() => ({
  paper: {
    padding: 24,
  },
  gridContainer: {
    padding: 8,
  },
  drawGridHeader: {
    marginLeft: 25,
    marginRight: 25,
  },
  drawDetailsDialog: {
    '& > .MuiDialog-container': {
      '& > .MuiDialog-paper.MuiDialog-paperFullWidth': {
        minHeight: '62%',
      },
    },
  },
  drawGridCell: {
    textTransform: 'capitalize',
    fontSize: '14px',
    fontFamily: '"system-ui", "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
  },
  button: {
    border: `1px solid rgba(40, 83, 145, 1)`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    fontSize: 14,
    fontWeight: 500,
    color: 'rgba(40, 83, 145, 1)',
    padding: '4px 8px',
    marginLeft: 10,
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: 'rgba(237, 237, 237, 1)'
    }
  },
  link: {
    textDecoration: 'underline',
    color: 'rgba(66, 132, 243, 1)',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    }
  },
}));

const noOpComparator = () => 0;

const columns: AgGridColumnProps[] = [
  {
    field: 'drawNumber',
    headerName: 'Draw #',
    minWidth: 100,
    cellRenderer: 'drawNumberCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'drawSubtype',
    headerName: 'Draw Type',
    minWidth: 100,
    cellRenderer: 'drawTypeCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'stage',
    headerName: 'Stage',
    minWidth: 100,
    cellRenderer: 'drawStageCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'stage',
    headerName: 'Status',
    minWidth: 190,
    cellRenderer: 'drawStatusCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'creationDate',
    headerName: 'Draw Submission Date',
    minWidth: 100,
    cellRenderer: 'dateCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'amount',
    headerName: 'Amount Approved',
    minWidth: 100,
    cellRenderer: 'amountRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'wireSentDate',
    headerName: 'Wire Sent Date',
    minWidth: 100,
    cellRenderer: 'wireSentDateRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'wireAmount',
    headerName: 'Wire Amount',
    minWidth: 100,
    cellRenderer: 'wireAmountRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    maxWidth: 100,
    cellRenderer: 'actionsCellRenderer',
    comparator: noOpComparator,
    sortable: false,
  },
];

const DRAWN_STATUSES = {
  "Draw In Review": { chipColor: 'rgba(229, 246, 253, 1)', textColor: 'rgba(1, 67, 97, 1)' },
  "Draw Approved": { chipColor: 'rgba(229, 246, 253, 1)', textColor: 'rgba(1, 67, 97, 1)' },
  "Wire Being Prepared": { chipColor: 'rgba(229, 246, 253, 1)', textColor: 'rgba(1, 67, 97, 1)' },
  "Wire Sent/Draw Resolved": { chipColor: 'rgba(237, 247, 237, 1)', textColor: 'rgba(30, 70, 32, 1)' },
  "Draw Requested": { chipColor: 'rgba(229, 246, 253, 1)', textColor: 'rgba(1, 67, 97, 1)' },
}
interface PropertyDrawsAccordionProps {
  loanId: string;
  property: any;
  draws: Draw[];
  onDrawClick: (drawId: number) => void;
}

export const PropertyDrawsAccordion = observer(({ loanId, property, draws, onDrawClick }: PropertyDrawsAccordionProps) => {
  const classes = useStyles();
  const [drawId, setDrawId] = useState(null);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isViewPropertyDetailsOpen, setIsViewPropertyDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { drawStore } = useDrawsStore();
  const { userStore } = useUserStore();

  const drawStageProcessor = (drawStageArray, drawStage) => {
    const index = drawStageArray.findIndex(stage => stage === drawStage);
    if (index === -1) return '-';
    return `${index + 1} of ${drawStageArray.length}`;
  };

  const frameworkComponents = {
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value) : '';
    },
    drawStageCellRenderer: params => {
      const drawStageArray = params.node.data?.stageArray ?? [];
      const drawStage = params.node.data?.stage;
      return drawStageProcessor(drawStageArray, drawStage);
    },
    drawStatusCellRenderer: ({ value }) => {
      return <Chip label={value} style={{ backgroundColor: DRAWN_STATUSES[value]?.chipColor, color: DRAWN_STATUSES[value]?.textColor }} />;
    },
    drawTypeCellRenderer: ({ value }) => {
      return <Typography component='span' className={classes.drawGridCell}>{value}</Typography>;
    },
    drawNumberCellRenderer: params => {
      const drawId = params.node.data?.drawId;
      const value = getDrawNumber(params.node.data);
      return <Button variant='text' className={classes.link} unsetTextTransfrom onClick={() => onDrawClick(drawId)} testId='open-draw'>{value}</Button>
    },
    amountRenderer: ({ value }) => {
      return value ? formatCurrency(value) : '';
    },
    wireAmountRenderer: ({ value }) => {
      return value && value > 0 ? formatCurrency(value) : '';
    },
    wireSentDateRenderer: params => {
      return (params.node.data?.wireAmount > 0 ? formatDate(params.node.data?.wireSentDate) : '');
    },
    actionsCellRenderer: params => {
      if (params.node.data?.stage !== DRAW_REQUEST_STAGE && params.node.data?.stage !== DRAW_INIT_STAGE) {
        return (
          <IconButton disabled>
            <MoreVert color="disabled" />
          </IconButton>
        );
      } else {
        return (
          <IconButton onClick={e => openMenu(e, params.node.data.drawId)}>
            <MoreVert color="primary" />
          </IconButton>
        );
      }
    },
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setDrawId(record);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setDrawId(null);
    setAnchorEl(null);
  };

  const cancelRequest = () => {
    drawStore.cancelDrawRequest(drawId, loanId);
    setIsConfirmOpen(false);
    handleMenuClose();
  };

  const onViewClick = (event) => {
    event.stopPropagation();
    setIsViewPropertyDetailsOpen(true);
  }

  const sortedDraws = [...draws];

  try {
    sortedDraws.sort((a, b) =>
      dayjs(b.creationDate, 'MM/DD/YYYY HH:mm:ss.SSS').valueOf() -
      dayjs(a.creationDate, 'MM/DD/YYYY HH:mm:ss.SSS').valueOf()
    );
  } catch (err) {
    console.error("There was an error sorting the draws");
    console.error(err);
  }

  return (
    <>
      <DrawsAccordion title={property.address} onViewClick={userStore.isNportalUser ? onViewClick : null}>
        <DataGrid
          key={drawStore.drawListKey}
          columns={columns}
          rows={sortedDraws}
          frameworkComponents={frameworkComponents}
          domLayout="autoHeight"
        />
      </DrawsAccordion>
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <div>
          <MenuItem
            testId={`cancel-draw-request-${drawId}`}
            onClick={() => {
              setAnchorEl(null);
              setIsConfirmOpen(true);
            }}
          >
            <ListItemIcon>
              <Cancel fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel Request</ListItemText>
          </MenuItem>
        </div>
      </Menu>
      <ConfirmDialog
        open={isConfirmOpen}
        title={`Cancel Draw Request`}
        body={`Are you sure you want to cancel the draw request?\n\nThis Operation cannot be reverted.`}
        handleCancel={() => setIsConfirmOpen(false)}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={cancelRequest}
        confirmButtonText={'Confirm'}
      />

      <StandardDialog
        open={isViewPropertyDetailsOpen}
        onClose={() => setIsViewPropertyDetailsOpen(false)}
        handleClose={() => setIsViewPropertyDetailsOpen(false)}
        maxWidth="md"
        title={'Property Details'}
        subTitle={property?.address}
        dialogContent={<ViewPropertyDetails loanId={loanId} property={property} />}
      />
    </>
  );
});
